<template>
  <v-container>
    <the-purchase-condition-confirm-dialog :pending-redirection="pendingRedirection" />

    <supplier-search-form :loading="isLoading" :search="searchAndLoadData" />

    <card-wrapper class="mt-10" v-if="supplierInfo">
      <selection-header
        :supplier-corp-name="supplierInfo.supplierCorpName"
        :has-modification="isSelectionInProgress"
        :loading="isLoading"
        @validate="validateModifications"
      >
        <template slot="available-text">
          {{ $tc('page.selection.available', supplierInfo.purchaseConditionCount) }}
        </template>

        <template slot="modification-text">
          <div class="success--text">
            {{ $tc('page.selection.selectedCount', this.selectedIds.length) }}
          </div>
          <div class="warning--text">
            {{ $tc('page.selection.unselectedCount', this.unselectedIds.length) }}
          </div>
        </template>
      </selection-header>

      <conditional-wrapper :condition="dataLoading">
        <template v-slot:truthy>
          <circuit-tabs-loader />
        </template>
        <template v-slot:falsy>
          <circuit-tabs :supplier-purchase-conditions="purchaseConditions" :duplicates="duplicates">
            <template v-slot="{ purchaseConditions }">
              <selection-table
                :purchase-conditions="purchaseConditions"
                :current-selection="selectedIds"
                :current-unselection="unselectedIds"
                :row-click="togglePurchaseConditionSelection"
              />
            </template>
            <template v-slot:duplicates="{ purchaseConditions }">
              <selection-table
                :purchase-conditions="purchaseConditions"
                :current-selection="selectedIds"
                :current-unselection="unselectedIds"
                :row-click="togglePurchaseConditionSelection"
                duplicates-mod
              />
            </template>
          </circuit-tabs>
        </template>
      </conditional-wrapper>
    </card-wrapper>
  </v-container>
</template>

<script lang="ts">
import Vue from 'vue';
import { RawLocation } from 'vue-router';

import CardWrapper from '@/components/interface/card-wrapper.vue';
import ConditionalWrapper from '@/components/interface/conditional-wrapper.vue';
import CircuitTabs from '@/components/selection/circuit-tabs.vue';
import CircuitTabsLoader from '@/components/selection/circuit-tabs-loader.vue';
import SelectionHeader from '@/components/selection/selection-header.vue';
import SelectionTable from '@/components/selection/selection-table.vue';
import SupplierSearchForm from '@/components/selection/supplier-search-form.vue';
import ThePurchaseConditionConfirmDialog from '@/components/selection/the-purchase-condition-confirm-dialog.vue';
import { PurchaseCondition } from '@/models/purchase-condition/purchase-condition';
import { PurchaseConditionsGroupByCircuit } from '@/models/purchase-condition/purchase-conditions-group-by-circuit';
import { SupplierInfo } from '@/models/purchase-condition/selection/supplier-info';
import { extractDuplicates, loadPurchaseConditions, saveModifications, searchSupplierInfo } from '@/services/selection';
import { Status } from '@/models/purchase-condition/selection/status-enum';

type PendingRedirectionType = ((to?: RawLocation | false | ((vm: Vue) => any) | void) => void) | null;

export default Vue.extend({
  name: 'Selection',
  components: {
    CircuitTabsLoader,
    ConditionalWrapper,
    CardWrapper,
    CircuitTabs,
    SelectionHeader,
    SelectionTable,
    ThePurchaseConditionConfirmDialog,
    SupplierSearchForm
  },
  data() {
    return {
      dataLoading: false,
      validationLoading: false,
      supplierInfo: null as SupplierInfo | null,
      purchaseConditions: {} as PurchaseConditionsGroupByCircuit,
      selectedIds: [] as string[],
      unselectedIds: [] as string[],
      pendingRedirection: null as PendingRedirectionType
    };
  },
  computed: {
    isLoading(): boolean {
      return this.validationLoading || this.dataLoading;
    },
    isSelectionInProgress(): boolean {
      return this.selectedIds.length > 0 || this.unselectedIds.length > 0;
    },
    duplicates(): PurchaseCondition[] {
      return extractDuplicates(this.purchaseConditions);
    }
  },
  async created() {
    window.addEventListener('beforeunload', event => {
      if (this.isSelectionInProgress) {
        event.returnValue = 'modal';
      }
    });
  },
  beforeRouteLeave(_to, _from, next) {
    if (this.isSelectionInProgress) {
      this.pendingRedirection = next;
    } else {
      next();
    }
  },
  methods: {
    togglePurchaseConditionSelection(purchaseCondition: PurchaseCondition): void {
      const selectedIndex = this.selectedIds.indexOf(purchaseCondition.id);
      if (selectedIndex > -1) {
        this.selectedIds.splice(selectedIndex, 1);
        return;
      }

      const unselectedIndex = this.unselectedIds.indexOf(purchaseCondition.id);
      if (unselectedIndex > -1) {
        this.unselectedIds.splice(unselectedIndex, 1);
        return;
      }

      const { selection } = purchaseCondition;
      const isInitiallySelected = selection != null && selection.status === Status.SELECTED;
      if (isInitiallySelected) {
        this.unselectedIds.push(purchaseCondition.id);
      } else {
        this.selectedIds.push(purchaseCondition.id);
      }
    },
    async searchAndLoadData(supplierCode?: number, supplierCorpName?: string) {
      this.dataLoading = true;
      try {
        this.supplierInfo = await searchSupplierInfo(supplierCode, supplierCorpName);
        await this.loadSupplierPurchaseCondition();
      } finally {
        this.dataLoading = false;
      }
    },
    async validateModifications() {
      this.validationLoading = true;
      try {
        await saveModifications(this.selectedIds, this.unselectedIds);
        this.selectedIds = [];
        this.unselectedIds = [];
        await this.loadSupplierPurchaseCondition();
      } finally {
        this.validationLoading = false;
      }
    },
    async loadSupplierPurchaseCondition() {
      if (this.supplierInfo?.supplierCode) {
        this.purchaseConditions = (await loadPurchaseConditions(this.supplierInfo.supplierCode)) ?? {};
      }
    }
  }
});
</script>
